import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Award, Calendar, ExternalLink, Star, Trophy, Target } from 'lucide-react';

const Certifications = ({ data }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, rotateY: -10 },
    visible: {
      opacity: 1,
      y: 0,
      rotateY: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const getCertIcon = (title) => {
    if (title.includes('Hackathon') || title.includes('Top')) return Trophy;
    if (title.includes('CTF')) return Target;
    return Award;
  };

  const getCertColor = (title) => {
    if (title.includes('CEH')) return 'text-red-400 border-red-400/30 bg-red-400/10';
    if (title.includes('TryHackMe')) return 'text-green-400 border-green-400/30 bg-green-400/10';
    if (title.includes('Hackathon')) return 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10';
    if (title.includes('CTF')) return 'text-purple-400 border-purple-400/30 bg-purple-400/10';
    return 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10';
  };

  return (
    <section id="certifications" className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-xs" />
            <Trophy className="w-8 h-8 text-yellow-400 mx-4" />
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-xs" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-yellow-400 mb-4">
            Certifications & Achievements
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Professional certifications and competitive achievements in cybersecurity, ethical hacking, and penetration testing.
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {data.map((cert, index) => {
            const IconComponent = getCertIcon(cert.title);
            const colorClasses = getCertColor(cert.title);
            
            return (
              <motion.div
                key={cert.id}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.02, 
                  rotateY: 5,
                  z: 10
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="group"
              >
                <Card className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:border-yellow-400/50 transition-all duration-300 h-full overflow-hidden">
                  <div className="relative">
                    {/* Certificate Image */}
                    <div className="relative overflow-hidden h-48">
                      <img 
                        src={cert.image} 
                        alt={cert.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Icon Overlay */}
                      <div className={`absolute top-4 right-4 p-3 rounded-full border-2 ${colorClasses} backdrop-blur-sm`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Title and Issuer */}
                        <div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors duration-300">
                            {cert.title}
                          </h3>
                          <p className="text-lg font-semibold text-gray-300">
                            {cert.issuer}
                          </p>
                        </div>

                        {/* Date and Credential ID */}
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center text-gray-400">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>{cert.date}</span>
                          </div>
                          <Badge className="bg-gray-700 text-gray-300 border-gray-600">
                            ID: {cert.credentialId}
                          </Badge>
                        </div>

                        {/* Description */}
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {cert.description}
                        </p>

                        {/* Skills */}
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2 flex items-center">
                            <Star className="w-4 h-4 text-yellow-400 mr-1" />
                            Key Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {cert.skills.map((skill, skillIndex) => (
                              <Badge 
                                key={skillIndex}
                                variant="outline" 
                                className={`text-xs ${colorClasses} border-opacity-50`}
                              >
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Verification Link (if applicable) */}
                        {cert.credentialId && (
                          <motion.div
                            className="pt-2 border-t border-gray-700"
                            whileHover={{ scale: 1.02 }}
                          >
                            <button 
                              className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors duration-300 text-sm font-medium"
                              onClick={() => alert('Certificate verification link would open here')}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Verify Certificate
                            </button>
                          </motion.div>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Achievement Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { label: 'Certifications', value: '4+', icon: Award },
              { label: 'CTF Wins', value: '15+', icon: Trophy },
              { label: 'Hackathons', value: '10+', icon: Target },
              { label: 'Security Tools', value: '25+', icon: Star }
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  className="text-center p-6 bg-gray-800/30 border border-gray-700 rounded-lg backdrop-blur-sm hover:border-yellow-400/50 transition-all duration-300"
                >
                  <IconComponent className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Certifications;